<script>
  import HeaderComponent from '@/components/Header.vue';
  import FooterComponent from '@/components/Footer.vue';
  import ParticlesBackground from '@/components/ParticlesBackground.vue';

  export default {
    name: 'HomeView',
    components: {
      HeaderComponent,
      FooterComponent,
      ParticlesBackground
    },
    data() {
      return {
        currentProjectIndex: 0,
        autoScrollInterval: null,
        projects: [],
        isLoadingProjects: true,
        clients: [],
        isLoadingClients: true,
        services: [
          {name:"Branding", icon:"ti-medall", description:"You want to be pretty online and impress everyone. Let our digital makeup artists do it for you.", delay: 400},
          {name:"Design", icon:"icon-pencil", description:"We shape ideas into stunning visuals that speak for you. Let’s craft something unforgettable.", delay: 250},
          {name:"Website", icon:"icon-globe", description:"We create your website to convey your values and image online. Tell us about you, we do the cooking.", delay: 100}
        ]
      };
    },
    methods: {
      async loadProjects() {
        try {
          this.isLoadingProjects = true;
          
          // Try to load from NocoDB first
          try {
            const nocoService = (await import('@/services/nocodb.js')).default;
            const data = await nocoService.getProjects();
            
            if (data && data.list && data.list.length > 0) {
              // Sort by release date (newest first) and limit to 6
              const sortedProjects = data.list
                .sort((a, b) => {
                  // Parse YYYY-MM format to create comparable dates
                  const parseDate = (dateStr) => {
                    if (!dateStr) return new Date('2020-01');
                    // Handle YYYY-MM format by converting to YYYY-MM-01 for Date constructor
                    return new Date(dateStr + '-01');
                  };
                  
                  const dateA = parseDate(a.releaseDate);
                  const dateB = parseDate(b.releaseDate);
                  return dateB - dateA;
                })
                .slice(0, 6)
                .map(project => ({
                  ...project,
                  link: `/projects/${project.slug}`,
                  image: project.image && project.image.length > 0
                    ? `https://web-db.drods.ch/${project.image[0].signedPath || project.image[0].path}`
                    : 'https://images.unsplash.com/photo-1754039985001-ccafee437736?w=150&h=150&fit=crop&crop=face&auto=format',
                  technologies: typeof project.technologies === 'string' 
                    ? project.technologies.split(', ').map(tech => tech.trim())
                    : project.technologies || []
                }));
              
              this.projects = sortedProjects;
            }
          } catch (nocoError) {
            // Silently handle NocoDB connection issues
          }
          
          this.isLoadingProjects = false;
        } catch (error) {
          this.isLoadingProjects = false;
        }
      },
      async loadClients() {
        try {
          this.isLoadingClients = true;
          
          // Try to load from NocoDB first
          try {
            const nocoService = (await import('@/services/nocodb.js')).default;
            const data = await nocoService.getClients();
            
            if (data && data.list && data.list.length > 0) {
              // Sort by order_index and map to expected format
              this.clients = data.list
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map(client => ({
                  href: client.url,
                  image: client.logo && client.logo.length > 0
                    ? `https://web-db.drods.ch/${client.logo[0].signedPath || client.logo[0].path}`
                    : 'https://images.unsplash.com/photo-1754039985001-ccafee437736?w=150&h=150&fit=crop&crop=face&auto=format',
                  alt: client.alt_text || `${client.name} logo`
                }));
            }
          } catch (nocoError) {
            // Silent error
          }
          
          this.isLoadingClients = false;
        } catch (error) {
          this.isLoadingClients = false;
        }
      },
      nextProject() {
        this.currentProjectIndex = (this.currentProjectIndex + 1) % this.projects.length;
        this.resetAutoScroll();
      },
      previousProject() {
        this.currentProjectIndex = this.currentProjectIndex === 0 
          ? this.projects.length - 1 
          : this.currentProjectIndex - 1;
        this.resetAutoScroll();
      },
      goToProject(index) {
        this.currentProjectIndex = index;
        this.resetAutoScroll();
      },
      startAutoScroll() {
        this.autoScrollInterval = setInterval(() => {
          this.nextProject();
        }, 4000); // 4 seconds
      },
      resetAutoScroll() {
        if (this.autoScrollInterval) {
          clearInterval(this.autoScrollInterval);
        }
        this.startAutoScroll();
      },
      addStructuredData() {
        // Add organization structured data
        const organizationData = {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Diginova",
          "url": "https://diginova.ch",
          "logo": "https://diginova.ch/assets/img/logo-250.jpg",
          "sameAs": [
            "https://www.instagram.com/diginova_ch",
            "https://www.facebook.com/diginova.ch",
            "https://www.linkedin.com/company/diginova"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+41-XX-XXX-XX-XX",
            "contactType": "customer service",
            "email": "contact@diginova.ch"
          },
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "CH"
          },
          "description": "Swiss digital agency creating stunning websites and digital experiences with expert branding, design, and development services."
        };

        // Add website structured data
        const websiteData = {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Diginova",
          "url": "https://diginova.ch",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://diginova.ch/projects?search={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        };

        this.addJsonLdToHead(organizationData);
        this.addJsonLdToHead(websiteData);
      },
      addJsonLdToHead(data) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      }
    },
    mounted() {
      // Load projects first, then start auto-scroll
      this.loadProjects().then(() => {
        if (this.projects.length > 0) {
          this.startAutoScroll();
        }
      });
      
      // Load clients
      this.loadClients();

      // Add structured data for SEO
      this.addStructuredData();

      // Cache jQuery elements
      const $window = $(window);
      const $document = $(document);
      const htmlBody = $("html,body");
      const windowWidth = $window.width();


      /*===============================================
        2. Scroll Animations
      ===============================================*/
      sal({
        duration: 500
      });


      /*===============================================
        3. SmoothScroll
      ===============================================*/
      $('a[href*="#"]').not('[href="#"]').not('[data-toggle="tab"]').on("click", function(e) {
        if (
          location.pathname.replace(/^\//, '') == this.pathname.replace(/^\//, '') 
          && 
          location.hostname == this.hostname
        ) {
          var target = $(this.hash);
          target = target.length ? target : $('[name=' + this.hash.slice(1) + ']');
          if (target.length) {
            htmlBody.animate({scrollTop: target.offset().top}, 450, "easeInOutQuart");
            e.preventDefault();
          }
        }
      });




      /*===============================================
        5. Header Menu
      ===============================================*/
      const header = $(".header.fixed");
      if (header.length) {
        let lastScrollTop = 0;
        let headerScrolling = false;
        
        $window.on("scroll", () => {
          if (!headerScrolling) {
            headerScrolling = true;
            requestAnimationFrame(() => {
              const currentScrollTop = $window.scrollTop();
              
              if (lastScrollTop < currentScrollTop && currentScrollTop > 210) {
                header.addClass("hide");
              } else if (lastScrollTop > currentScrollTop && currentScrollTop > 210) {
                header.removeClass("hide");
              }
              
              lastScrollTop = currentScrollTop;
              headerScrolling = false;
            });
          }
        });
      }

      //
      // Sticky //
      //
      if ($(".header.sticky:not(.header-lg, .header-xl, .absolute-light, .absolute-dark)").length) {
        $("<div class='header-placeholder'></div>").insertBefore(".header.sticky");
      }
      if ($(".header-lg.sticky:not(.absolute-light, .absolute-dark)").length) {
        $("<div class='header-placeholder-lg'></div>").insertBefore(".header-lg.sticky");
      }
      if ($(".header-xl.sticky:not(.absolute-light, .absolute-dark)").length) {
        $("<div class='header-placeholder-xl'></div>").insertBefore(".header-xl.sticky");
      }

      //
      // Absolute //
      //
      if ($(".absolute-light").length) {
        $(window).on("scroll", function() {
          var headerFixed = $(".header.fixed");
          if ($(window).scrollTop() > 10) {
            headerFixed.removeClass("absolute-light");
          }
          else {
            headerFixed.addClass("absolute-light");
          }
        });
      }
      if ($(".absolute-dark").length) {
        $(window).on("scroll", function() {
          var headerFixed = $(".header.fixed");
          if ($(window).scrollTop() > 10) {
            headerFixed.removeClass("absolute-dark");
          }
          else {
            headerFixed.addClass("absolute-dark");
          }
        });
      }

      //
      // Open/Close Header Menu //
      //
      var wrapper = $(".wrapper");
      var headerMenu = $(".header-menu-wrapper");
      var mToggle = $(".m-toggle");
      var closeBtn = $(".close-button");

      mToggle.on("click", function() {
        if (headerMenu.hasClass("show")) {
          headerMenu.removeClass("show");
          wrapper.removeClass("overlay");
        }
        else {
          headerMenu.addClass("show");
          wrapper.addClass("overlay");
        }
      });

      closeBtn.on("click", function() {
        if (headerMenu.hasClass("show")) {
          headerMenu.removeClass("show");
          wrapper.removeClass("overlay");
        }
      });
      $(document).on("click", function(e) {
        if ( $(e.target).closest(".header-menu-wrapper, .m-toggle").length === 0 ) {
          if (headerMenu.hasClass("show")) {
            headerMenu.removeClass("show");
            wrapper.removeClass("overlay");
          }
        }
      });

      //
      // Move Header Menu on mobile //
      //
      checkSize();
      $(window).resize(checkSize);
      function checkSize(){
        if (headerMenu.css("position") == "fixed" ) {
          headerMenu.prependTo("body");
        }
        else {
          $(".header>.container, .header>.container-fluid").append(headerMenu);
        }
      }

      //
      // Dropdown Menu //
      //
      $(".m-link").each(function() {
        var $this = $(this);
        if ($this.parent(".m-item").children(".m-dropdown").length) {
          $this.addClass("m-dropdown-toggle");
          var dropdownMenu = $this.parent(".m-item").children(".m-dropdown");

          $this.on("click", function(e) {
            if ($this.hasClass("active")) {
              $this.removeClass("active");
              dropdownMenu.removeClass("show");
            }
            else {
              $this.addClass("active");
              dropdownMenu.addClass("show");
            }
            e.preventDefault();
          });
        }
      });

      //
      // Sub Dropdown Menu //
      //
      $(".m-dropdown-link").each(function() {
        var $this = $(this);

        if ($this.parent(".m-dropdown-item").children(".m-subdropdown").length) {
          $this.addClass("m-subdropdown-toggle");
          var subDropdownMenu = $this.parent(".m-dropdown-item").children(".m-subdropdown");

          $this.on("click", function(e) {
            if ($this.hasClass("active")) {
              $this.removeClass("active");
              subDropdownMenu.removeClass("show");
            }
            else {
              $this.addClass("active");
              subDropdownMenu.addClass("show");
            }
            e.preventDefault();
          });
        }
      });


      /*===============================================
        9. Parallax Background
      ===============================================*/
      if (windowWidth > 1200) {
        const parallaxBg = $(".parallax-bg");
        parallaxBg.each(function() {
          $(this).parallaxie({
            speed: 0.2
          });
        });
      }


      /*===============================================
        10. Background Image
      ===============================================*/
      $(".bg-image").each(function() {
        const $this = $(this);
        const bgData = $this.attr("data-bg-src");
        if (bgData) {
          $this.css('background-image', `url("${bgData}")`);
        }
      });


      /*===============================================
        11. Slider
      ===============================================*/
      $(".owl-carousel").each(function() {
        var $carousel = $(this);

        var $defaults = {
          rewind: true,
          navText: ["<i class='ti-angle-left'></i>","<i class='ti-angle-right'></i>"],
          autoHeight: true, 
          autoplayTimeout: 2000, 
          autoplaySpeed: 300, 
          autoplayHoverPause: true, 
          navSpeed: 350, 
          dotsSpeed: 350, 
          dragEndSpeed: 350
        }
        var $options = {
          items: $carousel.data("owl-items"),
          margin: $carousel.data("owl-margin"),
          loop: $carousel.data("owl-loop"),
          center: $carousel.data("owl-center"),
          nav: $carousel.data("owl-nav"),
          rewind: $carousel.data("owl-rewind"),
          dots: $carousel.data("owl-dots"),
          autoplay: $carousel.data("owl-autoplay")
        }
        var $responsive = {
          responsive: {
            0 : {
              items: $carousel.data("owl-xs")
            },
            576 : {
              items: $carousel.data("owl-sm")
            },
            768 : {
              items: $carousel.data("owl-md")
            },
            992 : {
              items: $carousel.data("owl-lg")
            },
            1200 : {
              items: $carousel.data("owl-xl")
            }
          }
        }

        if ($carousel.hasClass("portfolio-carousel")) {
          var $portfolioCarousel = {
            items: 2, 
            center: true
          }
        }

        if ($carousel.hasClass("product-carousel")) {
          var $productCarousel = {
            items:1,
            animateOut: 'fadeOut',
            URLhashListener:true,
            startPosition: 'URLHash'
          }
        }

        $carousel.owlCarousel( $.extend( $defaults, $options, $responsive, $portfolioCarousel, $productCarousel) );

        var customPrev = $("#customPrev");
        var customNext = $("#customNext");

        customNext.on("click", function(){
          $carousel.trigger("next.owl.carousel", [400]);
        });
        customPrev.on("click", function(){
          $carousel.trigger("prev.owl.carousel", [400]);
        });

      });
    },
    beforeUnmount() {
      // Clean up auto-scroll interval
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
      }
    }
  }
</script>

<style scoped>
/* Carousel slide transitions */
.slide-enter-active, .slide-leave-active {
  transition: all 0.5s ease-in-out;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Loading ripple animation for home page */
.lds-ripple,
.lds-ripple div {
  box-sizing: border-box;
}

.lds-ripple {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}

.lds-ripple div {
  position: absolute;
  border: 4px solid var(--color-accent);
  opacity: 1;
  border-radius: 50%;
  animation: lds-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
}

.lds-ripple div:nth-child(2) {
  animation-delay: -0.5s;
  border-color: rgba(255, 255, 255, 0.8);
}

@keyframes lds-ripple {
  0% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 0;
  }
  4.9% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 0;
  }
  5% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 1;
  }
  100% {
    top: 0;
    left: 0;
    width: 80px;
    height: 80px;
    opacity: 0;
  }
}
</style>

<template>
  <HeaderComponent />
  <!-- Hero section with TailwindCSS -->
  <div class="relative h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black overflow-hidden">
    <ParticlesBackground />
    <div class="relative z-10 container mx-auto px-4 text-center">
      <h1 class="text-4xl md:text-6xl lg:text-7xl font-heading font-semibold text-white uppercase tracking-wider leading-tight">
        Blending digital and reality
      </h1>
    </div>
  </div>
  <!-- end Hero section -->

  <!-- About section -->
  <div class="py-20">
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div class="order-2 lg:order-1">
          <div class="relative">
            <img src="/assets/img/team-picture.jpg" alt="Diginova team collaborating on digital design projects in modern Swiss office" class="w-full h-auto rounded-lg shadow-lg" loading="lazy">
          </div>
        </div>
        <div class="order-1 lg:order-2">
          <h6 class="text-sm font-medium uppercase tracking-wider text-secondary mb-4">Little About</h6>
          <h2 class="text-4xl font-bold font-heading text-gray-900 mb-6">Who We Are</h2>
          <p class="text-gray-600 leading-relaxed mb-10">We're a creative team that listens first and designs second. Affordable, thoughtful, and tailored to your needs—because great design should work for you, not the other way around.</p>
          <router-link to="/about" class="inline-block bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300">Read more</router-link>
        </div>
      </div>
    </div>
  </div>
  <!-- end About section -->

  <!-- Services section -->
  <div class="mx-auto mt-2 mb-12 max-w-6xl text-center p-6">
    <h2 class="mb-12 text-center text-4xl font-extrabold text-gray-900 sm:text-5xl font-heading">
      Our Services
    </h2>
    <div class="mx-auto max-w-5xl items-stretch space-y-4 text-left sm:flex sm:space-y-0 sm:space-x-8 sm:text-center">
      <div 
        v-for="service in services" 
        :key="service.name"
        class="flex w-full flex-col items-center text-center rounded-xl border border-[#d1d5db] border-opacity-50 px-6 py-8 text-gray-900 duration-300 hover:border-primary hover:shadow-xl transform hover:scale-105"
        data-sal="slide-up" 
        :data-sal-delay="service.delay"
      >
        <div class="mb-4 flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary bg-opacity-10 rounded-full">
          <i :class="service.icon" class="text-2xl sm:text-3xl text-white"></i>
        </div>
        <div class="font-bold text-gray-900 text-lg mb-3 font-heading">{{ service.name }}</div>
        <div class="text-sm text-gray-600 leading-relaxed">{{ service.description }}</div>
      </div>
    </div>
  </div>
  <!-- end Services section -->

  <!-- Portfolio section -->
  <div class="bg-gradient-to-br from-primary to-secondary py-20">
    <div class="container mx-auto px-4">
      <h2 class="!mb-8 text-center text-4xl font-extrabold text-white sm:text-5xl font-heading">
        Our Projects
      </h2>
      
      <!-- Loading State -->
      <div v-if="isLoadingProjects" class="py-16">
        <div class="flex flex-col items-center justify-center">
          <div class="lds-ripple">
            <div></div>
            <div></div>
          </div>
          <p class="text-white/80 mt-4">Loading our latest projects...</p>
        </div>
      </div>

      <!-- Carousel Container -->
      <div v-else-if="projects.length > 0" class="relative max-w-4xl mx-auto">
        <!-- Project Display -->
        <div class="relative overflow-hidden rounded-2xl shadow-2xl">
          <transition name="slide" mode="out-in">
            <div :key="currentProjectIndex" class="relative">
              <router-link :to="`/projects/${projects[currentProjectIndex].slug}`" class="block group">
                <div class="aspect-video relative overflow-hidden">
                  <img 
                    :src="projects[currentProjectIndex].image" 
                    :alt="`${projects[currentProjectIndex].name} - ${projects[currentProjectIndex].category} project by Diginova`"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="eager"
                  >
                  <!-- Overlay -->
                  <div class="absolute inset-0 bg-gradient-to-t from-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                
                <!-- Project Info -->
                <div class="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div class="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 class="text-3xl font-bold font-heading mb-2">{{ projects[currentProjectIndex].name }}</h3>
                    <p class="text-accent text-lg font-medium">{{ projects[currentProjectIndex].category }}</p>
                  </div>
                </div>
              </router-link>
            </div>
          </transition>
        </div>
        
        <!-- Dots Indicator with Navigation -->
        <div class="flex items-center justify-center gap-6 mt-8">
          <!-- Previous Arrow -->
          <button 
            @click="previousProject"
            class="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 !rounded-full transition-all duration-300 group"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          
          <!-- Dots -->
          <div class="flex !space-x-2">
            <button
              v-for="(_, index) in projects"
              :key="index"
              @click="goToProject(index)"
              :class="[
                'w-3 h-3 !rounded-full !border-0 !p-0 !transition-all !duration-300',
                index === currentProjectIndex
                  ? '!bg-accent !scale-125'
                  : '!bg-white/30 hover:!bg-white/50'
              ]"
            ></button>
          </div>
          
          <!-- Next Arrow -->
          <button 
            @click="nextProject"
            class="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 !rounded-full transition-all duration-300 group"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
        
        <!-- See More Button -->
        <div class="text-center mt-12">
          <router-link 
            to="/projects"
            class="inline-flex items-center bg-white hover:bg-accent text-primary hover:text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group"
          >
            See All Our Projects
            <svg class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
            </svg>
          </router-link>
        </div>
      </div>
    </div>
  </div>
  <!-- end Portfolio section -->

  <!-- Clients section -->
  <div class="py-20 bg-gray-50">
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h6 class="text-sm font-medium uppercase tracking-wider text-secondary mb-4">THEY</h6>
          <h2 class="text-4xl font-bold font-heading text-gray-900 mb-6">Trusted Us</h2>
          <p class="text-gray-600 leading-relaxed">There are some amazing clients that give us the opportunity to bring their image to the web.</p>
        </div>
        <div>
          <!-- 3x3 Grid with dashed borders -->
          <div class="grid grid-cols-3 gap-0 max-w-md mx-auto">
            <div 
              v-for="(_, index) in clients" 
              :key="index"
              :class="[
                'aspect-square flex items-center justify-center p-6',
                // Right border for columns 1 and 2
                index % 3 !== 2 ? 'border-r border-dashed border-[#d1d5db]/50' : '',
                // Bottom border for rows 1 and 2
                index < 6 ? 'border-b border-dashed border-[#d1d5db]/50' : ''
              ]"
            >
              <a 
                v-if="clients[index]" 
                :href="clients[index].href" 
                target="_blank" 
                class="block hover:opacity-75 transition-opacity"
              >
                <img 
                  :src="clients[index].image" 
                  :alt="`${clients[index].alt} - Trusted partner of Diginova digital agency`" 
                  class="max-w-full max-h-full object-contain"
                  loading="lazy"
                >
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- end Clients section -->

  <!-- Parallax section -->
  <div class="py-54 flex items-center justify-center bg-cover bg-center bg-fixed relative" style="background-image: url('/assets/img/pexels-sanketgraphy-15598923.jpg')">
    <div class="absolute inset-0 bg-dark/50"></div>
    <div class="relative z-10">
      <div class="container mx-auto px-4 text-center">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-5xl md:text-6xl lg:text-7xl font-bold font-heading text-white mb-12" data-sal="slide-up" data-sal-delay="200">We Are Ready, Are You?</h2>
          <div class="mt-12" data-sal="slide-up" data-sal-delay="400">
            <router-link to="/contact" class="inline-block border-2 border-white text-white hover:bg-white hover:text-dark px-12 py-6 text-xl font-medium transition-all duration-300 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105">Get In Touch</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- end Parallax section -->
  <FooterComponent />
</template>